import { useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, Check, QrCode } from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { adminMutate, apiRead, Select, useToast } from '../admin/primitives.js'
import { Field, TextInput } from '../admin/form.js'
import { Link } from '../router.js'
import { CHEMIN_TABLETTES, cheminTablette, dashboardsDe, dashboardsPour, ID_TABLETTE_RX, identifiantPropose, messageErreur, SALLES, type DashboardHa } from './tablettes-modele.js'
import './tablettes.css'

/**
 * TECHNIQUE › TABLETTES › ENRÔLER — /technique/tablettes/enroler (02/11/2026).
 *
 * Demande d'Hugo : « enrôler des nouvelles tablettes hyper facilement, avec
 * QR code direct depuis Gestion ». Trois étapes : décrire, scanner, prête.
 *
 * Le QR porte un jeton à usage unique valable 30 min ET le mot de passe Wi-Fi :
 * il arrive du serveur déjà dessiné (SVG), n'est jamais stocké, n'entre dans
 * aucun cache, et disparaît de la page dès le scan, l'expiration ou le départ
 * (le jeton est alors annulé chez kiosk-api).
 */

type Etape =
  | { n: 1 }
  | { n: 2; jeton: string; expire: number; svg: string; statut: 'attente' | 'scanne' }
  | { n: 3; tabletteId: string | null }

export function EnrolementPage() {
  const meta = useShellMeta()
  const toast = useToast()
  const [etape, setEtape] = useState<Etape>({ n: 1 })
  const [nom, setNom] = useState('')
  const [idSaisi, setIdSaisi] = useState<string | null>(null)
  const [salle, setSalle] = useState('')
  const [mode, setMode] = useState<'murale' | 'mobile'>('murale')
  // Profils Wi-Fi gardés par kiosk-api : Gestion ne connaît que leur nom (le SSID).
  const [profils, setProfils] = useState<string[] | null>(null)
  const [wifi, setWifi] = useState('')
  const [dashs, setDashs] = useState<DashboardHa[] | null>(null)
  // Comptes Home Assistant de tablette (noms seuls) : le compte choisi filtre les vues proposées.
  const [comptes, setComptes] = useState<string[] | null>(null)
  const [compte, setCompte] = useState('')
  const [dash, setDash] = useState('')
  const [envoi, setEnvoi] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const [expire, setExpire] = useState(false)
  const id = idSaisi ?? identifiantPropose(nom)

  useEffect(() => {
    void apiRead<{ profils?: string[] }>('/api/v1/tablettes/wifi').then((r) => {
      const p = r.status === 200 && Array.isArray(r.data.profils) ? r.data.profils : []
      setProfils(p)
      setWifi((w) => w || p[0] || '')
    }).catch(() => setProfils([]))
  }, [])
  useEffect(() => {
    void apiRead<{ comptes?: string[] }>('/api/v1/tablettes/ha/comptes').then((r) => setComptes(r.status === 200 && Array.isArray(r.data.comptes) ? r.data.comptes : [])).catch(() => setComptes([]))
    void apiRead<unknown>('/api/v1/tablettes/dashboards').then((r) => setDashs(r.status === 200 ? dashboardsDe(r.data) : [])).catch(() => setDashs([]))
  }, [])
  const optionsDash = useMemo(() => [{ value: '', label: 'Par défaut (choisi par kiosk-api)' },
    ...dashboardsPour(dashs ?? [], compte || null).flatMap((d) => d.vues.map((v) => ({ value: `${d.id}|${v.id}`, label: `${d.titre} › ${v.titre}` })))], [dashs, compte])

  const manque = !nom.trim() ? 'Donnez un nom à la tablette.'
    : !ID_TABLETTE_RX.test(id) || id.startsWith('-') ? 'Identifiant : minuscules, chiffres et « - », 40 caractères au plus.'
      : !salle ? 'Choisissez l’emplacement.'
        : !wifi ? (profils && profils.length === 0 ? 'kiosk-api ne propose aucun profil Wi-Fi.' : 'Choisissez le réseau Wi-Fi.') : null

  const generer = async () => {
    if (manque || envoi) return
    setEnvoi(true); setErreur(null); setExpire(false)
    const [dd, vv] = dash ? dash.split('|') : []
    const r = await adminMutate<{ jeton_id?: string; expire_le?: string; qr_svg?: string; error?: string; message?: string }>('POST', '/api/v1/tablettes/enrolements', {
      id, nom: nom.trim(), salle, mode, wifi, ...(compte ? { ha_compte: compte } : {}), ...(dd && vv ? { dashboard: { dashboard: dd, vue: vv } } : {}),
    }, { deconnexionExplicite: true })
    setEnvoi(false)
    if (r.status === 200 && r.data.jeton_id && r.data.qr_svg && r.data.expire_le) {
      setEtape({ n: 2, jeton: r.data.jeton_id, expire: Date.parse(r.data.expire_le), svg: r.data.qr_svg, statut: 'attente' })
      return
    }
    setErreur(r.data.error === 'STEPUP_REQUIRED' ? 'Confirmation d’identité annulée : aucun QR n’a été créé.' : messageErreur(r.status, r.data, 'Le QR n’a pas été créé.'))
  }

  // Suivi du scan, toutes les 2 s, tant que le QR est affiché.
  const jeton = etape.n === 2 ? etape.jeton : null
  useEffect(() => {
    if (!jeton) return
    let vivant = true
    const suivre = async () => {
      const r = await apiRead<{ statut?: string; tablette_id?: string | null }>(`/api/v1/tablettes/enrolements/${encodeURIComponent(jeton)}`).catch(() => null)
      if (!vivant || !r || r.status !== 200) return
      if (r.data.statut === 'pret') { setEtape({ n: 3, tabletteId: r.data.tablette_id ?? null }); return }
      if (r.data.statut === 'expire' || r.data.statut === 'annule') { setEtape({ n: 1 }); setExpire(r.data.statut === 'expire'); return }
      if (r.data.statut === 'scanne') setEtape((e) => (e.n === 2 ? { ...e, svg: '', statut: 'scanne' } : e))
    }
    const t = setInterval(() => { void suivre() }, 2_000)
    return () => { vivant = false; clearInterval(t) }
  }, [jeton])

  // Quitter la page avec un QR affiché l'annule chez kiosk-api.
  const enAttente = useRef<string | null>(null)
  enAttente.current = etape.n === 2 && etape.statut === 'attente' ? etape.jeton : null
  useEffect(() => () => {
    if (enAttente.current) void adminMutate('DELETE', `/api/v1/tablettes/enrolements/${encodeURIComponent(enAttente.current)}`).catch(() => undefined)
  }, [])

  const annuler = async () => {
    if (etape.n === 2) {
      const r = await adminMutate<{ error?: string; message?: string }>('DELETE', `/api/v1/tablettes/enrolements/${encodeURIComponent(etape.jeton)}`)
      if (r.status >= 300) toast('error', messageErreur(r.status, r.data, 'Le QR n’a pas pu être annulé : il expirera seul.'))
    }
    setEtape({ n: 1 })
  }

  return (
    <Shell active="technique" meta={meta}>
      <main className="lc-page lc-page--readable lc-stagger tb-page tb-enrol">
        <header className="tb-tete__titre">
          <Link className="lc-crumb" href={CHEMIN_TABLETTES}>← Tablettes</Link>
          <h1 className="lc-title"><QrCode size={26} aria-hidden="true" /> Enrôler une tablette</h1>
          <p className="lc-subtitle">Trois étapes : décrire la tablette, scanner le QR code, c’est prêt.</p>
        </header>

        <ol className="tb-etapes" aria-label="Étapes">
          {['Décrire', 'Scanner le QR', 'Prête'].map((l, i) => {
            const n = i + 1
            const cls = n === etape.n ? ' tb-etape--cours' : n < etape.n ? ' tb-etape--faite' : ''
            return <li key={l} className={`tb-etape${cls}`} aria-current={n === etape.n ? 'step' : undefined}><span className="tb-etape__n">{n < etape.n ? <Check size={14} aria-hidden="true" /> : n}</span>{l}</li>
          })}
        </ol>

        {etape.n === 1 ? (
          <section className="lc-panel tb-panneau" aria-label="Décrire la tablette">
            {expire ? <p className="tb-bandeau" role="status">Le QR a expiré sans être scanné. Générez-en un nouveau.</p> : null}
            <div className="tb-form">
              <Field label="Nom"><TextInput value={nom} onChange={setNom} maxLength={60} placeholder="Bar ROOM 3" ariaLabel="Nom" autoFocus /></Field>
              <Field label="Identifiant" helper="Proposé à partir du nom ; c’est aussi son compte sur le broker, il ne change plus ensuite.">
                <TextInput value={id} onChange={(v) => setIdSaisi(v)} maxLength={64} ariaLabel="Identifiant" />
              </Field>
              <Field label="Emplacement"><Select value={salle} placeholder="Choisir" options={SALLES.map((s) => ({ value: s, label: s }))} onChange={setSalle} ariaLabel="Emplacement" /></Field>
              <Field label="Réseau Wi-Fi" helper="Le mot de passe reste chez kiosk-api ; il voyage seulement dans le QR.">
                <Select value={wifi} placeholder={profils === null ? 'Lecture…' : 'Choisir'} options={(profils ?? []).map((w) => ({ value: w, label: w }))} onChange={setWifi} ariaLabel="Réseau Wi-Fi" />
              </Field>
              {comptes && comptes.length ? (
                <Field label="Compte Home Assistant" helper="Le compte avec lequel la tablette ouvre Home Assistant ; il limite les vues proposées.">
                  <Select value={compte} placeholder="Choisi par kiosk-api" options={[{ value: '', label: 'Choisi par kiosk-api' }, ...comptes.map((c) => ({ value: c, label: c }))]} onChange={(v) => { setCompte(v); setDash('') }} ariaLabel="Compte Home Assistant" />
                </Field>
              ) : null}
              <Field label="Dashboard au démarrage">
                <Select value={dash} options={optionsDash} onChange={setDash} ariaLabel="Dashboard au démarrage" />
              </Field>
            </div>
            <div className="tb-modes" role="radiogroup" aria-label="Usage">
              <label className={`tb-mode${mode === 'murale' ? ' tb-mode--on' : ''}`}>
                <input type="radio" name="mode" checked={mode === 'murale'} onChange={() => setMode('murale')} />
                <span><b>Tablette murale</b><span className="lc-hint">Kiosque verrouillé, écran piloté depuis Gestion.</span></span>
              </label>
              <label className={`tb-mode${mode === 'mobile' ? ' tb-mode--on' : ''}`}>
                <input type="radio" name="mode" checked={mode === 'mobile'} onChange={() => setMode('mobile')} />
                <span><b>Tablette mobile</b><span className="lc-hint">Pas de veille programmée, alerte batterie plus tôt.</span></span>
              </label>
            </div>
            {erreur ? <p className="tb-bandeau tb-bandeau--fort" role="alert">{erreur}</p> : null}
            <div className="tb-actions tb-actions--fin">
              {manque && nom ? <span className="lc-hint">{manque}</span> : null}
              <button type="button" className="lc-btn lc-btn--primary" disabled={!!manque || envoi} onClick={() => { void generer() }}>{envoi ? 'Création du QR…' : 'Générer le QR code'}</button>
            </div>
          </section>
        ) : null}

        {etape.n === 2 ? (
          <section className="tb-qr" aria-label="Scanner le QR code">
            <div className="lc-panel tb-qr__carte">
              {etape.statut === 'attente' && etape.svg ? (
                <img className="tb-qr__image" src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(etape.svg)}`} alt="QR code d’enrôlement" />
              ) : <div className="tb-qr__scanne"><Check size={40} aria-hidden="true" /><b>QR scanné</b><span className="lc-hint">La tablette s’installe…</span></div>}
              <p className="tb-qr__statut" role="status">
                <span className={`tb-point tb-fond--${etape.statut === 'attente' ? 'info' : 'ok'}`} aria-hidden="true" />
                {etape.statut === 'attente' ? <>En attente du scan · expire dans <Rebours fin={etape.expire} /></> : 'Scanné : installation de l’app et connexion au Wi-Fi…'}
              </p>
              <button type="button" className="lc-btn" onClick={() => { void annuler() }}>Annuler</button>
            </div>
            <div className="tb-qr__aide">
              <section className="lc-panel tb-panneau">
                <h2 className="tb-h2">Sur la tablette</h2>
                <ol className="tb-liste">
                  <li>Tablette neuve ou réinitialisée : sur l’écran d’accueil, toucher 6 fois au même endroit.</li>
                  <li>Scanner ce QR code. La tablette installe l’app, rejoint {wifi} et se verrouille.</li>
                  <li>Cette page passe à « Prête » dès que la tablette répond.</li>
                </ol>
              </section>
              <p className="tb-avert"><AlertTriangle size={18} aria-hidden="true" /> Ce QR code sert une seule fois et expire dans 30 minutes. Il contient le mot de passe Wi-Fi : ne le photographiez pas. Il disparaît après le scan, et Gestion ne le garde pas.</p>
              <p className="lc-hint">{nom.trim()} · {salle} · {mode === 'murale' ? 'Tablette murale' : 'Tablette mobile'} · Wi-Fi {wifi}</p>
            </div>
          </section>
        ) : null}

        {etape.n === 3 ? (
          <section className="lc-panel tb-pret" aria-label="Tablette prête">
            <span className="tb-pret__coche" aria-hidden="true"><Check size={28} /></span>
            <div>
              <b className="tb-pret__titre">{nom.trim() || 'La tablette'} est prête</b>
              <p className="lc-hint">Elle apparaît dans le parc et se pilote depuis sa fiche.</p>
            </div>
            <div className="tb-actions">
              <button type="button" className="lc-btn" onClick={() => { setNom(''); setIdSaisi(null); setSalle(''); setDash(''); setEtape({ n: 1 }) }}>En enrôler une autre</button>
              <Link className="lc-btn lc-btn--primary" href={etape.tabletteId ? cheminTablette(etape.tabletteId) : CHEMIN_TABLETTES}>Ouvrir sa fiche</Link>
            </div>
          </section>
        ) : null}
      </main>
    </Shell>
  )
}

function Rebours({ fin }: { fin: number }) {
  const [, setTic] = useState(0)
  useEffect(() => { const t = setInterval(() => setTic((n) => n + 1), 1000); return () => clearInterval(t) }, [])
  const s = Math.max(0, Math.round((fin - Date.now()) / 1000))
  return <b className="tb-mono">{String(Math.floor(s / 60)).padStart(2, '0')}:{String(s % 60).padStart(2, '0')}</b>
}
